import ArrowBackIcon from '@material-design-icons/svg/filled/arrow_back.svg?react';
import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import KeyboardArrowDownIcon from '@material-design-icons/svg/filled/keyboard_arrow_down.svg?react';
import KeyboardArrowUpIcon from '@material-design-icons/svg/filled/keyboard_arrow_up.svg?react';
import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import * as Tabs from '@radix-ui/react-tabs';
import { useStore } from '@tanstack/react-store';
import { type ReactNode, use, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { TRPCContext } from '../../contexts/TRPC';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useRejectionReasons } from '../../hooks/useRejectionReasons';
import { shouldPollAutomatedReview } from '../../lib/hubspot-management/automatedReview';
import { getHubspotTicketUrl } from '../../lib/hubspot-management/formatters';
import { cn } from '../../lib/utils';
import { HUBSPOT_TICKETS_PAGE_SIZE } from '../../stores/hubspotManagementStore';
import {
  type HubspotPanelTab,
  hubspotPanelStore,
  setActiveTab,
  toggleAttestation,
  toggleTokenJson,
} from '../../stores/hubspotPanelStore';
import { getAdminDappPath } from '../../utils/dappRoutes';
import { highlightJson } from '../../utils/jsonUtils';
import { HubspotDappIcon } from '../hubspot-management/HubspotDappIcon';
import {
  Chip,
  getTicketDisplayName,
  getTicketVersionLabel,
  SourceChip,
  StatusChip,
  SubmissionKindChip,
} from '../hubspot-management/table/HubspotTicketCells';
import { useHubspotTicketAssignment } from './hooks/useHubspotTicketAssignment';
import { useHubspotTicketAutomatedReview } from './hooks/useHubspotTicketAutomatedReview';
import { useHubspotTicketDetail } from './hooks/useHubspotTicketDetail';
import { useHubspotTicketKeyboardNavigation } from './hooks/useHubspotTicketKeyboardNavigation';
import { useHubspotTicketMediaLoading } from './hooks/useHubspotTicketMediaLoading';
import { useHubspotTicketNotes } from './hooks/useHubspotTicketNotes';
import { useHubspotTicketStatus } from './hooks/useHubspotTicketStatus';
import { useModeratorActions } from './hooks/useModeratorActions';
import { useModeratorCapabilities } from './hooks/useModeratorCapabilities';
import { HubspotTicketActionBar } from './layout/HubspotTicketActionBar';
import { HubspotTicketAssigneeControl } from './layout/HubspotTicketAssigneeControl';
import { PanelSection } from './PanelSection';
import { HubspotTicketAutomatedReviewSection } from './sections/automation/HubspotTicketAutomatedReviewSection';
import { HubspotTicketAuditSection } from './sections/collaboration/HubspotTicketAuditSection';
import { HubspotTicketNotesSection } from './sections/collaboration/HubspotTicketNotesSection';
import { HubspotTicketWorkflowSection } from './sections/collaboration/HubspotTicketWorkflowSection';
import { AndroidDetailsSection } from './sections/details/AndroidDetailsSection';
import { HubspotTicketDetailsSection } from './sections/details/HubspotTicketDetailsSection';
import { HubspotTicketGeofilteringNotice } from './sections/details/HubspotTicketGeofilteringNotice';
import { NftAddressesSection } from './sections/details/NftAddressesSection';
import { PayloadSection } from './sections/details/PayloadSection';
import { HubspotTicketMediaSection } from './sections/media/HubspotTicketMediaSection';
import {
  HubspotTicketChecksSummary,
  HubspotTicketSubmissionFacts,
} from './sections/overview/HubspotTicketOverviewSection';
import {
  AndroidDetailsSkeleton,
  AutomationSkeleton,
  HeaderSkeleton,
  MediaSkeleton,
} from './SkeletonLoaders';
import { HubspotTicketReviewChecklist } from './status/HubspotTicketReviewChecklist';
import { hubspotTicketReviewChecklistItems } from './status/reviewChecklist';
import type { HubspotTicketDetailPanelProps } from './types';
import { getHubspotTicketDerivedData } from './utils';

const CHECKS_DOT_CLASS: Record<string, string> = {
  Passed: 'bg-emerald-400',
  Failed: 'bg-red-400',
  Error: 'bg-rose-400',
  Running: 'bg-amber-300 animate-pulse',
  Queued: 'bg-sky-300 animate-pulse',
  Stale: 'bg-neutral-500',
  Canceled: 'bg-neutral-500',
};

const navButtonClassName =
  'inline-flex size-8 items-center justify-center rounded-md text-neutral-300 transition-colors hover:bg-neutral-800 hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent';

function PanelTab({
  value,
  count,
  dotClassName,
  children,
}: {
  value: HubspotPanelTab;
  count?: number;
  dotClassName?: string;
  children: string;
}) {
  return (
    <Tabs.Trigger
      value={value}
      className="group relative flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap px-3 font-medium text-neutral-400 transition-colors hover:text-neutral-200 focus-visible:outline-none data-[state=active]:text-white"
    >
      {children}
      {dotClassName && (
        <span
          aria-hidden="true"
          className={cn('size-1.5 rounded-full', dotClassName)}
        />
      )}
      {count != null && count > 0 && (
        <span className="rounded-full bg-neutral-800 px-1.5 text-[11px] leading-4 text-neutral-300 group-data-[state=active]:bg-teal-500/20 group-data-[state=active]:text-teal-200">
          {count}
        </span>
      )}
      <span
        aria-hidden="true"
        className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-teal-400 opacity-0 transition-opacity group-data-[state=active]:opacity-100"
      />
    </Tabs.Trigger>
  );
}

function TabPanel({
  value,
  children,
}: {
  value: HubspotPanelTab;
  children: ReactNode;
}) {
  return (
    <Tabs.Content
      value={value}
      className="min-h-0 flex-1 overflow-y-auto focus-visible:outline-none"
    >
      <div className="mx-auto w-full max-w-4xl space-y-4 p-4 sm:p-5">
        {children}
      </div>
    </Tabs.Content>
  );
}

export function HubspotTicketDetailPanel({
  ticketId,
  viewerRole,
  viewerUserId,
  onClose,
  currentIndex,
  pageSize,
  total,
  currentPage,
  totalPages,
  onNavigate,
  onTicketUpdated,
}: HubspotTicketDetailPanelProps) {
  const trpc = use(TRPCContext);
  // Decision controls live in the header on wide screens and in a sticky
  // bottom bar on phones; rendered once so dialogs never mount twice.
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const { data, isLoading, error, refetch } = useHubspotTicketDetail({
    ticketId,
    trpc,
  });
  const {
    data: automatedReview,
    isLoading: isAutomatedReviewLoading,
    error: automatedReviewError,
    refetch: refetchAutomatedReview,
  } = useHubspotTicketAutomatedReview({
    runId: data?.latestReviewRunId ?? null,
    trpc,
  });
  const {
    statusOptions,
    selectedStageId,
    currentStageId,
    statusPipelineLabel,
    statusError,
    isStatusLoading,
    isStatusSaving,
    isClosingStage,
    isRejectionClosure,
    closureCategory,
    closureLabel,
    closureRejectionReasons,
    reviewerNote,
    setSelectedStageId,
    setClosureCategory,
    setClosureLabel,
    toggleClosureRejectionReason,
    setReviewerNote,
    handleSaveStatus,
  } = useHubspotTicketStatus({
    ticketId,
    trpc,
    data,
    refetchTicket: refetch,
    onTicketUpdated,
  });
  const {
    selectedAssignedUserId,
    isAssignmentSaving,
    assignmentError,
    setSelectedAssignedUserId,
    handleSaveAssignment,
  } = useHubspotTicketAssignment({
    ticketId,
    trpc,
    data,
    refetchTicket: refetch,
    onTicketUpdated,
  });
  const isModerator = viewerRole === 'moderator';
  const rejectionReasonState = useRejectionReasons(trpc);
  const {
    capabilities: moderatorCapabilities,
    rejectionFailureAssigneeUserId,
    isLoading: isCapabilitiesLoading,
  } = useModeratorCapabilities({ viewerRole, trpc });
  const moderatorActions = useModeratorActions({
    ticketId,
    trpc,
    viewerUserId,
    data,
    statusOptions,
    rejectionReasonOptions: rejectionReasonState.rejectionReasonOptions,
    capabilities: moderatorCapabilities,
    rejectionFailureAssigneeUserId,
    refetchTicket: refetch,
    onTicketUpdated,
  });
  const { noteBody, isNoteSaving, noteError, setNoteBody, handleSaveNote } =
    useHubspotTicketNotes({
      ticketId,
      trpc,
      refetchTicket: refetch,
      onTicketUpdated,
    });
  const {
    dappStoreData,
    androidDetails,
    categorizedPermissions,
    banner,
    featureGraphic,
    screenshots,
    videos,
    files,
    updateAuthority,
  } = getHubspotTicketDerivedData(data);
  const apkFile = files.find((f) => f.purpose === 'install') ?? files[0];
  const apkFileUrl = apkFile?.uri ?? null;
  const assignableUsers = data?.assignableUsers ?? [];
  const orderedAssignableUsers = [
    ...assignableUsers.filter((user) => user.id === viewerUserId),
    ...assignableUsers.filter((user) => user.id !== viewerUserId),
  ];
  const {
    mediaLoadingStates,
    markBannerLoaded,
    markFeatureGraphicLoaded,
    markScreenshotLoaded,
    markVideosLoaded,
  } = useHubspotTicketMediaLoading({
    ticketId,
    trpc,
    data,
    banner,
    featureGraphic,
    screenshots,
    videos,
  });

  const activeTab = useStore(hubspotPanelStore, (state) => state.activeTab);
  const isTokenJsonExpanded = useStore(
    hubspotPanelStore,
    (state) => state.isTokenJsonExpanded,
  );
  const isAttestationExpanded = useStore(
    hubspotPanelStore,
    (state) => state.isAttestationExpanded,
  );
  const isPermissionsExpanded = useStore(
    hubspotPanelStore,
    (state) => state.isPermissionsExpanded,
  );
  const [completedChecklistItemIds, setCompletedChecklistItemIds] = useState<
    string[]
  >([]);
  const [expandedChecklistItemIds, setExpandedChecklistItemIds] = useState<
    string[]
  >([]);

  const isClosureDialogOpen =
    isClosingStage &&
    Boolean(selectedStageId) &&
    selectedStageId !== currentStageId;

  useHubspotTicketKeyboardNavigation({
    onClose,
    onNavigate,
    enabled: !moderatorActions.isRejecting && !isClosureDialogOpen,
  });

  useEffect(() => {
    const latestStatus =
      automatedReview?.status ?? data?.latestAutomatedReviewSummary?.status;

    if (!shouldPollAutomatedReview(latestStatus)) {
      return;
    }

    const interval = globalThis.setInterval(() => {
      void refetch();
      void refetchAutomatedReview();
    }, 15_000);

    return () => {
      globalThis.clearInterval(interval);
    };
  }, [
    automatedReview?.status,
    data?.latestAutomatedReviewSummary?.status,
    refetch,
    refetchAutomatedReview,
  ]);

  useEffect(() => {
    setCompletedChecklistItemIds([]);
    setExpandedChecklistItemIds([]);
  }, [ticketId]);

  const requiresChecklist =
    isModerator && (moderatorCapabilities?.requiresReviewChecklist ?? true);
  const isChecklistBlocking =
    requiresChecklist &&
    completedChecklistItemIds.length !==
      hubspotTicketReviewChecklistItems.length;
  const canViewAuditTrail =
    viewerRole === 'admin' ||
    viewerRole === 'superadmin' ||
    (isModerator && (moderatorCapabilities?.canViewAuditTrail ?? false));
  const canEditAssignee =
    !isModerator || (moderatorCapabilities?.canReassignTickets ?? false);
  const checklistHelperText = isChecklistBlocking
    ? 'Complete the review checklist before saving a status change.'
    : null;

  const handleCopyJson = (jsonData: unknown) => {
    navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2));
    toast.success('Copied to clipboard');
  };

  const position =
    currentIndex >= 0
      ? (currentPage - 1) * HUBSPOT_TICKETS_PAGE_SIZE + currentIndex + 1
      : null;
  // Navigation crosses page boundaries, so only the absolute ends disable.
  const isFirst = currentIndex <= 0 && currentPage <= 1;
  const isLast = currentIndex >= pageSize - 1 && currentPage >= totalPages;
  const displayName = data ? getTicketDisplayName(data) : 'Ticket';
  const versionLabel = data ? getTicketVersionLabel(data) : null;
  const packageName = androidDetails?.android_package ?? data?.dappPackageName;
  const dappPath =
    data?.dappId || packageName
      ? getAdminDappPath({
          id: data?.dappId ?? packageName!,
          packageName,
        })
      : null;
  const summary = data?.latestAutomatedReviewSummary ?? null;
  const mediaCount =
    (banner ? 1 : 0) +
    (featureGraphic ? 1 : 0) +
    screenshots.length +
    videos.length;

  const actionBar =
    isModerator && isCapabilitiesLoading ? (
      <div
        aria-hidden="true"
        className="h-8 w-44 animate-pulse rounded-lg bg-neutral-800"
      />
    ) : (
      <HubspotTicketActionBar
        isModerator={isModerator}
        moderatorActions={isModerator ? moderatorActions : undefined}
        rejectionReasonState={rejectionReasonState}
        isChecklistBlocking={isChecklistBlocking}
        checklistProgress={
          requiresChecklist
            ? {
                completed: completedChecklistItemIds.length,
                total: hubspotTicketReviewChecklistItems.length,
              }
            : null
        }
        statusEditor={{
          hsPipelineStage: data?.hsPipelineStage ?? null,
          statusPipelineLabel,
          statusOptions,
          selectedStageId,
          currentStageId,
          statusError,
          isStatusLoading,
          isStatusSaving,
          isClosingStage,
          isRejectionClosure,
          closureCategory,
          closureLabel,
          closureRejectionReasons,
          rejectionReasonState,
          reviewerNote,
          isChecklistBlocking,
          checklistHelperText,
          onSelectedStageChange: setSelectedStageId,
          onClosureCategoryChange: setClosureCategory,
          onClosureLabelChange: setClosureLabel,
          onToggleClosureRejectionReason: toggleClosureRejectionReason,
          onReviewerNoteChange: setReviewerNote,
          onSaveStatus: () => {
            void handleSaveStatus();
          },
        }}
      />
    );

  return (
    <div className="flex h-full min-h-0 flex-col bg-neutral-900">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-neutral-800/70 px-2 py-1.5 text-sm sm:px-3">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close ticket details"
          title="Close (Esc)"
          className="inline-flex h-9 items-center gap-1 rounded-lg px-2 text-neutral-300 transition-colors hover:bg-neutral-800 hover:text-white"
        >
          <ArrowBackIcon className="size-5 fill-current xl:hidden" />
          <span className="xl:hidden">Tickets</span>
          <CloseIcon className="hidden size-5 fill-current xl:block" />
        </button>
        <div className="flex items-center gap-1.5">
          {isLoading && data && (
            <span
              aria-label="Refreshing ticket"
              className="size-4 animate-spin rounded-full border-2 border-teal-400 border-t-transparent"
            />
          )}
          {data && (
            <a
              href={getHubspotTicketUrl(data.hubspotId)}
              target="_blank"
              rel="noopener noreferrer"
              title="Open in HubSpot"
              className="hidden items-center gap-1 rounded-md px-2 py-1 font-mono text-xs text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-teal-300 sm:inline-flex"
            >
              #{data.hubspotId}
              <OpenInNewIcon className="size-3.5 fill-current" />
            </a>
          )}
          <div className="flex items-center rounded-lg border border-neutral-800 bg-neutral-900/60">
            <button
              type="button"
              onClick={() => onNavigate('prev')}
              disabled={isFirst}
              aria-label="Previous ticket"
              title="Previous ticket (↑ or K)"
              className={navButtonClassName}
            >
              <KeyboardArrowUpIcon className="size-5 fill-current" />
            </button>
            <span className="px-1 text-xs tabular-nums text-neutral-400">
              {position ?? '–'} / {total}
            </span>
            <button
              type="button"
              onClick={() => onNavigate('next')}
              disabled={isLast}
              aria-label="Next ticket"
              title="Next ticket (↓ or J)"
              className={navButtonClassName}
            >
              <KeyboardArrowDownIcon className="size-5 fill-current" />
            </button>
          </div>
        </div>
      </div>

      <div className="shrink-0 border-b border-neutral-800 px-4 py-3 sm:px-5">
        {error ? (
          <div className="text-sm text-red-400">Failed to load ticket</div>
        ) : isLoading && !data ? (
          <HeaderSkeleton />
        ) : data ? (
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
            <div className="flex min-w-0 items-start gap-3">
              <HubspotDappIcon
                iconUrl={data.tokenIconUrl}
                name={displayName}
                className="size-12 rounded-xl"
              />
              <div className="min-w-0">
                <h2 className="truncate text-lg font-semibold leading-tight text-white">
                  {dappPath ? (
                    <Link
                      to={dappPath}
                      title="Open dapp page"
                      className="rounded-sm underline-offset-4 transition-colors hover:text-teal-300 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-teal-500"
                    >
                      {displayName}
                    </Link>
                  ) : (
                    displayName
                  )}
                </h2>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {versionLabel && (
                    <span className="text-xs text-neutral-400">
                      {versionLabel}
                    </span>
                  )}
                  <SubmissionKindChip ticket={data} />
                  <SourceChip source={data.submissionSource} />
                  <StatusChip ticket={data} />
                  {data.hsTicketPriority && (
                    <Chip className="border-neutral-700 text-neutral-300">
                      {data.hsTicketPriority}
                    </Chip>
                  )}
                  <HubspotTicketAssigneeControl
                    assignedUser={data.assignedUser}
                    assignableUsers={orderedAssignableUsers}
                    selectedAssignedUserId={selectedAssignedUserId}
                    isSaving={isAssignmentSaving}
                    error={assignmentError}
                    canEdit={canEditAssignee}
                    onChange={(userId) => {
                      setSelectedAssignedUserId(userId);
                      void handleSaveAssignment(userId);
                    }}
                  />
                </div>
              </div>
            </div>
            {isDesktop && actionBar}
          </div>
        ) : (
          <div className="text-sm text-neutral-400">
            No ticket data available
          </div>
        )}
      </div>

      {data && !error ? (
        <Tabs.Root
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as HubspotPanelTab)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <Tabs.List
            aria-label="Ticket sections"
            className="flex shrink-0 gap-1 overflow-x-auto border-b border-neutral-800 px-2 text-sm scrollbar-hide sm:px-3"
          >
            <PanelTab value="overview">Overview</PanelTab>
            <PanelTab
              value="checks"
              dotClassName={
                summary ? CHECKS_DOT_CLASS[summary.effectiveStatus] : undefined
              }
            >
              Checks
            </PanelTab>
            <PanelTab value="media" count={mediaCount}>
              Media
            </PanelTab>
            <PanelTab value="details">Details</PanelTab>
            <PanelTab value="activity" count={data.internalNotes.length}>
              Activity
            </PanelTab>
          </Tabs.List>

          <TabPanel value="overview">
            <HubspotTicketGeofilteringNotice
              availableCountriesMode={data.availableCountriesMode}
              availableCountries={data.availableCountries}
            />
            <HubspotTicketChecksSummary
              summary={summary}
              review={automatedReview}
              onOpenTab={setActiveTab}
            />
            {requiresChecklist && (
              <HubspotTicketReviewChecklist
                ticketId={ticketId}
                viewerRole={viewerRole}
                requestType={data.requestType}
                content={data.content}
                hasPublishedReleaseHistory={data.hasPublishedReleaseHistory}
                completedChecklistItemIds={completedChecklistItemIds}
                expandedChecklistItemIds={expandedChecklistItemIds}
                onToggleChecklistItem={(itemId) => {
                  setCompletedChecklistItemIds((currentIds) =>
                    currentIds.includes(itemId)
                      ? currentIds.filter((currentId) => currentId !== itemId)
                      : [...currentIds, itemId],
                  );
                }}
                onToggleChecklistGuidance={(itemId) => {
                  setExpandedChecklistItemIds((currentIds) =>
                    currentIds.includes(itemId)
                      ? currentIds.filter((currentId) => currentId !== itemId)
                      : [...currentIds, itemId],
                  );
                }}
              />
            )}
            <HubspotTicketSubmissionFacts
              data={data}
              androidDetails={androidDetails}
              categorizedPermissions={categorizedPermissions}
              apkFileUrl={apkFileUrl}
              links={{
                website: dappStoreData?.publisher_details?.website ?? null,
                privacyPolicy:
                  dappStoreData?.release_details?.privacy_policy_url ?? null,
                license: dappStoreData?.release_details?.license_url ?? null,
              }}
              onOpenTab={setActiveTab}
            />
          </TabPanel>

          <TabPanel value="checks">
            <HubspotTicketAutomatedReviewSection
              ticketId={ticketId}
              summary={summary}
              latestPolicyVersion={data.latestPolicyVersion}
              review={automatedReview}
              reviewError={automatedReviewError}
              isReviewLoading={isAutomatedReviewLoading}
              isExpanded={true}
              onToggle={() => {}}
              alwaysExpanded
              trpc={trpc}
              onRefetchReview={refetchAutomatedReview}
              onRefetchTicket={refetch}
              onTicketUpdated={onTicketUpdated}
            />
          </TabPanel>

          <TabPanel value="media">
            {mediaCount === 0 ? (
              <PanelSection>
                <div className="py-6 text-center text-sm text-neutral-500">
                  This submission has no media.
                </div>
              </PanelSection>
            ) : (
              <HubspotTicketMediaSection
                banner={banner}
                featureGraphic={featureGraphic}
                screenshots={screenshots}
                videos={videos}
                mediaLoadingStates={mediaLoadingStates}
                onBannerReady={markBannerLoaded}
                onFeatureGraphicReady={markFeatureGraphicLoaded}
                onScreenshotReady={markScreenshotLoaded}
                onVideosReady={markVideosLoaded}
              />
            )}
          </TabPanel>

          <TabPanel value="details">
            <HubspotTicketDetailsSection
              hubspotId={data.hubspotId}
              subject={data.subject}
              content={data.content}
              requestType={data.requestType}
              priority={data.hsTicketPriority}
              availableCountriesMode={data.availableCountriesMode}
              availableCountries={data.availableCountries}
            />
            <AndroidDetailsSection
              androidDetails={androidDetails}
              categorizedPermissions={categorizedPermissions}
              isPermissionsExpanded={isPermissionsExpanded}
            />
            {!isModerator && (
              <NftAddressesSection
                dappCollectionAccountAddress={data.dappCollectionAccountAddress}
                dappReleaseAccountAddress={data.dappReleaseAccountAddress}
                updateAuthority={updateAuthority}
                releaseMetadataUri={data.releaseMetadataUri}
              />
            )}
            {data.tokenJson && (
              <PayloadSection
                title="Token JSON"
                isExpanded={isTokenJsonExpanded}
                isLoading={isLoading && !!data}
                onToggle={toggleTokenJson}
                onCopy={() => handleCopyJson(data.tokenJson)}
              >
                <div className="max-h-96 overflow-y-auto rounded-lg border border-neutral-700/30 bg-neutral-950/60 p-4">
                  <pre className="whitespace-pre font-mono text-xs leading-relaxed text-neutral-300">
                    <code
                      dangerouslySetInnerHTML={{
                        __html: highlightJson(
                          JSON.stringify(data.tokenJson, null, 2),
                        ),
                      }}
                    />
                  </pre>
                </div>
              </PayloadSection>
            )}
            {!isModerator && data.attestationPayload && (
              <PayloadSection
                title="Attestation Payload"
                isExpanded={isAttestationExpanded}
                isLoading={isLoading && !!data}
                onToggle={toggleAttestation}
                onCopy={() => handleCopyJson(data.attestationPayload)}
              >
                <div className="max-h-96 overflow-y-auto rounded-lg border border-neutral-700/30 bg-neutral-950/60 p-4">
                  <pre className="whitespace-pre-wrap break-all font-mono text-xs leading-relaxed text-neutral-300">
                    {data.attestationPayload}
                  </pre>
                </div>
              </PayloadSection>
            )}
            {data.closureDetails && (
              <HubspotTicketWorkflowSection
                closureDetails={data.closureDetails}
              />
            )}
          </TabPanel>

          <TabPanel value="activity">
            <HubspotTicketNotesSection
              isLoading={isLoading && !!data}
              notes={data.internalNotes}
              noteBody={noteBody}
              isNoteSaving={isNoteSaving}
              noteError={noteError}
              onNoteBodyChange={setNoteBody}
              onSaveNote={() => {
                void handleSaveNote();
              }}
            />
            {canViewAuditTrail && (
              <HubspotTicketAuditSection
                isLoading={isLoading && !!data}
                auditEntries={data.auditEntries}
              />
            )}
          </TabPanel>
        </Tabs.Root>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
          {error ? (
            <div className="py-16 text-center">
              <div className="text-base font-semibold text-red-400">
                Couldn't load this ticket
              </div>
              <div className="mt-1 text-sm text-neutral-400">
                {error.message}
              </div>
            </div>
          ) : (
            <div className="mx-auto w-full max-w-4xl space-y-4">
              <AutomationSkeleton />
              <MediaSkeleton />
              <AndroidDetailsSkeleton />
            </div>
          )}
        </div>
      )}

      {data && !error && !isDesktop && (
        <div className="shrink-0 border-t border-neutral-800 bg-neutral-900/95 px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] backdrop-blur-sm">
          {actionBar}
        </div>
      )}
    </div>
  );
}
